import { Box, Typography } from '@mui/material';
import CustomPopOver from 'components/shared/CustomPopOver/CustomPopOver';
import { CompleteOperationType, StartOperationType } from 'model/alerts.model';
import { MouseEvent, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import RoutePaths from 'routes/RoutePaths';
import { getViewPath } from 'routes/utils';
import { BusinessesService } from 'service/businesses.service';
import zeplinColor from 'theme/Color';
import { MENU_OPTIONS } from './data';
import { styles } from './styles';
import { MenuOptionType, OptionComponentEnum } from './types';
import { usePermissions } from 'customHook/usePermissions';

export const OptionMenu = ({
  anchorElement,
  onClose,
  data,
  startOperation,
  completeOperation,
  getBusinesses,
  isMybusinessesTab,
}: {
  anchorElement: SVGSVGElement | null;
  onClose: () => void;
  data: {
    isTargeted: boolean;
    isFollowing: boolean;
    businessId: string;
    zoominfoCompanyId: string;
  };
  startOperation: StartOperationType;
  completeOperation: CompleteOperationType;
  getBusinesses: () => void;
  isMybusinessesTab: boolean;
}) => {
  const { isTargeted, isFollowing, businessId, zoominfoCompanyId } = data;
  const { hasAction, hasFullAccess } = usePermissions();
  const canViewDetails =
    hasAction('businesses', 'BusinessDetailsRelationship', 'view') || hasFullAccess('businesses');

  const canCreateMyBusiness =
    hasAction('businesses', 'mybusinesses', 'create') || hasFullAccess('businesses');
  const canCreateAllBusiness =
    hasAction('businesses', 'allbusinesses', 'create') || hasFullAccess('businesses');
  const canDeleteMyBusiness =
    hasAction('businesses', 'mybusinesses', 'delete') || hasFullAccess('businesses');
  const canDeleteAllBusiness =
    hasAction('businesses', 'allbusinesses', 'delete') || hasFullAccess('businesses');

  const visibleMenuOptions = MENU_OPTIONS.filter(({ id }) => {
    switch (id) {
      case 'view_details':
        return canViewDetails;
      case 'follow':
        return isMybusinessesTab ? canCreateMyBusiness : canCreateAllBusiness;
      case 'remove_account':
        return isMybusinessesTab ? canDeleteMyBusiness : canDeleteAllBusiness;
      default:
        return false;
    }
  });

  const [isFollow, setIsFollow] = useState(isFollowing);
  const [isTarget, setIsTarget] = useState(isTargeted);

  useEffect(() => {
    setIsFollow(isFollowing);
  }, [isFollowing]);

  useEffect(() => {
    setIsTarget(isTarget);
  }, [isTarget]);

  const targetId = useRef(businessId);

  const toggleTarget = async () => {
    try {
      if (!isTarget) {
        startOperation('delete', 'Removing Account...');
        await BusinessesService.removeTarget(businessId);
        targetId.current = zoominfoCompanyId;
        setIsTarget(!isTarget);
        completeOperation('delete', 'Account Removed');
      } else {
        startOperation('upload', 'Targeting Account...');
        const res = await BusinessesService.addTarget({
          zoominfoCompanyId: String(targetId.current),
          category: 'Brand',
        });
        targetId.current = res.business.businessId;
        completeOperation('upload', 'Account Targeted');
        setIsTarget(isTarget);
      }
      onClose();
      getBusinesses();
    } catch (error) {
      console.error('Failed to update target status', error);
      completeOperation('upload', String(error), true);
    }
  };

  const handleFollowAccount = async () => {
    if (!businessId) return;

    if (!isFollow) {
      startOperation('upload', 'Following Account...');
      await BusinessesService.followBusiness(businessId);
      setIsFollow(!isFollow);
      completeOperation('upload', 'Account Followed');
    } else {
      startOperation('delete', 'Unfollow Account...');
      await BusinessesService.unfollowBusiness(businessId);
      setIsFollow(!isFollow);
      completeOperation('delete', 'Account Unfollowed');
    }
    onClose();
    getBusinesses();
  };

  return (
    <CustomPopOver
      onClick={(e: MouseEvent<SVGSVGElement>) => {
        e.stopPropagation();
      }}
      setAnchorEl={onClose}
      anchorEl={anchorElement}
      id={'simple-popover'}
      sx={styles.popoverContainer}
    >
      <Box sx={styles.menuContainer}>
        {visibleMenuOptions.map(({ id, type, label, Icon }: MenuOptionType) => {
          const isLink = type === OptionComponentEnum.link;
          const component = isLink ? Link : 'button';
          let formattedLabel = label;

          let action;
          switch (id) {
            case 'follow':
              if (isFollow) formattedLabel = 'Unfollow';
              action = { onClick: () => handleFollowAccount() };
              break;
            case 'remove_account':
              if (isTarget) formattedLabel = 'Target Account';
              action = { onClick: () => toggleTarget() };
              break;
            case 'view_details':
              action = { to: getViewPath(RoutePaths.Businesses, businessId ? businessId : '') };
              break;
          }

          return (
            <Box sx={styles.optionContainer} key={label} component={component} {...action}>
              <Icon />
              <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                {formattedLabel}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </CustomPopOver>
  );
};
